import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, Check, X, Mail, Sparkles } from 'lucide-react';
import { userService } from '../services/UserService';
import { referralService } from '../services/ReferralService';
import { waitlistService } from '../services/WaitlistService';

const ACCESS_CODE_KEY = 'lunexx_access_granted';
const REFERRAL_CODE_KEY = 'lunexx_referral_code';

export default function AccessGatePage() {
  const navigate = useNavigate();
  const hasCheckedRef = useRef(false);

  // Access Code State
  const [accessKey, setAccessKey] = useState('');
  const [accessStatus, setAccessStatus] = useState('IDLE'); // IDLE, VALIDATING, GRANTED, ERROR
  const [accessError, setAccessError] = useState('');

  // Waitlist State
  const [email, setEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState('IDLE'); // IDLE, SUBMITTING, SUCCESS, ERROR
  const [waitlistError, setWaitlistError] = useState('');

  // Check for existing access on mount (including from referral link)
  useEffect(() => {
    const hasAccess = localStorage.getItem(ACCESS_CODE_KEY);
    if (hasAccess === 'true') {
      navigate('/discover');
    }
  }, [navigate]);

  // Pre-fill referral code from URL and auto-validate
  useEffect(() => {
    if (hasCheckedRef.current) return;

    const refCode = referralService.getReferralCodeFromURL();
    if (refCode) {
      hasCheckedRef.current = true;
      const normalizedCode = refCode.toUpperCase();
      setAccessKey(normalizedCode);
      localStorage.setItem(REFERRAL_CODE_KEY, normalizedCode);

      // Auto-validate
      autoValidateReferralCode(normalizedCode);
    }
  }, []);

  // Auto-validate referral code - grants access and redirects
  const autoValidateReferralCode = async (code) => {
    setAccessStatus('VALIDATING');

    try {
      const referralResult = await referralService.validateReferralCode(code);

      if (referralResult.valid) {
        localStorage.setItem(ACCESS_CODE_KEY, 'true');
        localStorage.setItem('lunexx_access_code', code);

        if (referralResult.isReferral) {
          localStorage.setItem(REFERRAL_CODE_KEY, code);
        }

        setAccessStatus('GRANTED');

        // Redirect after showing success
        setTimeout(() => {
          window.location.href = '/discover';
        }, 1000);
        return;
      }

      // Invalid - let user try manually (don't show error for auto-validation)
      setAccessStatus('IDLE');
    } catch (err) {
      console.error('Auto-validation error:', err);
      setAccessStatus('IDLE');
    }
  };

  const handleAccessSubmit = async (e) => {
    e.preventDefault();

    if (!accessKey.trim()) {
      setAccessError('Please enter an access code');
      setAccessStatus('ERROR');
      return;
    }

    setAccessStatus('VALIDATING');
    setAccessError('');

    try {
      // First try validating as referral code
      const referralResult = await referralService.validateReferralCode(accessKey);

      if (referralResult.valid) {
        localStorage.setItem(ACCESS_CODE_KEY, 'true');
        localStorage.setItem('lunexx_access_code', accessKey.toUpperCase().trim());

        if (referralResult.isReferral) {
          localStorage.setItem(REFERRAL_CODE_KEY, accessKey.toUpperCase().trim());
        }

        setAccessStatus('GRANTED');
        setTimeout(() => {
          window.location.href = '/discover';
        }, 1000);
        return;
      }

      // Fallback to regular access code validation
      const { valid, error: validationError } = await userService.validateAccessCode(accessKey);

      if (!valid) {
        setAccessError(validationError?.message || 'Invalid access code');
        setAccessStatus('ERROR');
        return;
      }

      await userService.useAccessCode(accessKey);
      localStorage.setItem(ACCESS_CODE_KEY, 'true');
      localStorage.setItem('lunexx_access_code', accessKey.toUpperCase().trim());

      setAccessStatus('GRANTED');
      setTimeout(() => {
        window.location.href = '/discover';
      }, 1000);
    } catch (err) {
      console.error('Access code validation error:', err);
      setAccessError('Something went wrong. Please try again.');
      setAccessStatus('ERROR');
    }
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setWaitlistError('Please enter your email');
      setWaitlistStatus('ERROR');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setWaitlistError('Please enter a valid email');
      setWaitlistStatus('ERROR');
      return;
    }

    setWaitlistStatus('SUBMITTING');
    setWaitlistError('');

    try {
      // Get referral code if present
      const referralCode = localStorage.getItem(REFERRAL_CODE_KEY) || null;

      const result = await waitlistService.addToWaitlist(email, 'access_gate', referralCode);

      if (!result.success) {
        setWaitlistError(result.error || 'Something went wrong');
        setWaitlistStatus('ERROR');
        return;
      }

      setWaitlistStatus('SUCCESS');
    } catch (err) {
      console.error('Waitlist signup error:', err);
      setWaitlistError('Something went wrong. Please try again.');
      setWaitlistStatus('ERROR');
    }
  };

  const handleAccessInputChange = (e) => {
    setAccessKey(e.target.value.toUpperCase());
    if (accessStatus === 'ERROR') {
      setAccessStatus('IDLE');
      setAccessError('');
    }
  };

  const handleEmailInputChange = (e) => {
    setEmail(e.target.value);
    if (waitlistStatus === 'ERROR') {
      setWaitlistStatus('IDLE');
      setWaitlistError('');
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0F0F0F] flex flex-col items-center justify-center px-6 relative overflow-hidden">

      {/* Ambient Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#A3E635] rounded-full blur-[300px] opacity-[0.03] pointer-events-none" />

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-[380px] flex flex-col items-center">

        {/* Logo */}
        <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-12 md:mb-16">
          LUNEXX<span className="text-[#A3E635]">.</span>
        </h1>

        {/* Access Code Section */}
        <div className="w-full mb-8">

          {accessStatus === 'GRANTED' ? (
            // Success State
            <div className="w-full h-[56px] bg-[#A3E635]/10 border border-[#A3E635]/30 rounded-2xl flex items-center justify-center gap-3">
              <Check className="w-5 h-5 text-[#A3E635]" />
              <span className="text-[#A3E635] font-mono text-sm uppercase tracking-widest">
                Access Granted
              </span>
            </div>
          ) : (
            // Input State
            <form onSubmit={handleAccessSubmit}>
              <div className={`relative w-full bg-[#181818] border rounded-2xl overflow-hidden transition-all duration-300
                ${accessStatus === 'ERROR'
                  ? 'border-red-500/50'
                  : 'border-white/10 hover:border-white/20 focus-within:border-[#A3E635]/50'}`}>

                <div className="flex items-center h-[56px]">
                  <span className={`pl-5 font-mono text-lg transition-colors ${accessStatus === 'ERROR' ? 'text-red-500' : 'text-zinc-600'}`}>
                    {accessStatus === 'VALIDATING' ? '' : '›'}
                  </span>

                  {accessStatus === 'VALIDATING' && (
                    <div className="pl-4">
                      <Loader2 className="w-5 h-5 text-[#A3E635] animate-spin" />
                    </div>
                  )}

                  <input
                    type="text"
                    value={accessKey}
                    onChange={handleAccessInputChange}
                    className={`flex-1 bg-transparent font-mono text-sm outline-none px-3 uppercase tracking-widest
                      ${accessStatus === 'ERROR'
                        ? 'text-red-400 placeholder:text-red-500/40'
                        : 'text-white placeholder:text-zinc-600'}`}
                    placeholder="ENTER_ACCESS_CODE"
                    autoComplete="off"
                    spellCheck="false"
                    disabled={accessStatus === 'VALIDATING'}
                  />

                  <button
                    type="submit"
                    disabled={!accessKey || accessStatus === 'VALIDATING'}
                    className={`h-10 w-10 mr-2 flex items-center justify-center rounded-xl transition-all duration-300
                      ${accessStatus === 'VALIDATING'
                        ? 'bg-zinc-800 cursor-not-allowed'
                        : 'bg-[#A3E635] text-black hover:bg-[#b5e865]'}
                      ${accessKey ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                  >
                    {accessStatus === 'VALIDATING' ? (
                      <Loader2 size={18} className="animate-spin text-zinc-400" />
                    ) : (
                      <ArrowRight size={18} />
                    )}
                  </button>
                </div>
              </div>

              {/* Access Error */}
              {accessStatus === 'ERROR' && accessError && (
                <div className="mt-3 flex items-center justify-center gap-2 text-red-400 text-xs font-mono">
                  <X size={12} />
                  <span>{accessError}</span>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Divider */}
        {accessStatus !== 'GRANTED' && (
          <div className="w-full flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-zinc-600 text-xs font-mono uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
        )}

        {/* Waitlist Section */}
        {accessStatus !== 'GRANTED' && (
          <div className="w-full">

            {waitlistStatus === 'SUCCESS' ? (
              // Success State
              <div className="w-full py-4 px-5 bg-[#181818] border border-white/10 rounded-2xl flex items-center justify-center gap-3">
                <Sparkles className="w-4 h-4 text-[#A3E635]" />
                <span className="text-zinc-300 text-sm font-mono">
                  You're on the list!
                </span>
              </div>
            ) : (
              // Input State
              <form onSubmit={handleWaitlistSubmit}>
                <div className={`relative w-full bg-[#181818] border rounded-2xl overflow-hidden transition-all duration-300
                  ${waitlistStatus === 'ERROR'
                    ? 'border-red-500/50'
                    : 'border-white/10 hover:border-white/20 focus-within:border-white/30'}`}>

                  <div className="flex items-center h-[56px]">
                    <div className="pl-5">
                      <Mail className={`w-4 h-4 transition-colors ${waitlistStatus === 'ERROR' ? 'text-red-500' : 'text-zinc-600'}`} />
                    </div>

                    <input
                      type="email"
                      value={email}
                      onChange={handleEmailInputChange}
                      className={`flex-1 bg-transparent text-sm outline-none px-3
                        ${waitlistStatus === 'ERROR'
                          ? 'text-red-400 placeholder:text-red-500/40'
                          : 'text-white placeholder:text-zinc-600'}`}
                      placeholder="Enter your email"
                      autoComplete="email"
                      disabled={waitlistStatus === 'SUBMITTING'}
                    />

                    <button
                      type="submit"
                      disabled={!email || waitlistStatus === 'SUBMITTING'}
                      className={`h-10 px-4 mr-2 flex items-center justify-center gap-2 rounded-xl text-xs font-medium uppercase tracking-wider transition-all duration-300
                        ${waitlistStatus === 'SUBMITTING'
                          ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                          : 'bg-white/10 text-white hover:bg-white/20'}
                        ${email ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}
                    >
                      {waitlistStatus === 'SUBMITTING' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <span>Join</span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Waitlist Error */}
                {waitlistStatus === 'ERROR' && waitlistError && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-red-400 text-xs font-mono">
                    <X size={12} />
                    <span>{waitlistError}</span>
                  </div>
                )}
              </form>
            )}
          </div>
        )}

        {/* Hint Text */}
        <p className="mt-8 text-zinc-600 text-[10px] font-mono uppercase tracking-widest text-center">
          Early access only
        </p>

      </div>

      {/* Back to Landing Link */}
      <a
        href={window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? '/?mode=landing'
          : 'https://lunexx.ai'}
        className="absolute bottom-8 text-zinc-600 text-xs font-mono hover:text-[#A3E635] transition-colors"
      >
        ← Back to site
      </a>

    </div>
  );
}